import { useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  ExclamationOutlined,
  StarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "../../styles/aiInsights.css";
import AIObservationModal from "../../components/modals/AIObservationModal";
import { useFetchFunction } from "../../utils/hooks/UseFetchUrl";

const SECTION_CONFIG = {
  success: {
    title: "What went well",
    subtitle: "Positive observations identified across your audits.",
    icon: <CheckOutlined />,
    apiKey: "went_well",
  },
  critical: {
    title: "What didn't go well",
    subtitle: "Negative observations / gaps identified across your audits.",
    icon: <ExclamationOutlined />,
    apiKey: "did_not_go_well",
  },
  warning: {
    title: "What can be improved",
    subtitle:
      "Areas where the audit findings indicate an opportunity for improvement.",
    icon: <StarOutlined />,
    apiKey: "can_be_improved",
  },
};

const getUpdatedTime = (date) => {
  if (!date) return "Not updated yet";

  const updatedDate = new Date(date);
  const now = new Date();

  const diffInSeconds = Math.floor((now - updatedDate) / 1000);

  if (diffInSeconds < 60) return "Just now";

  const diffInMinutes = Math.floor(diffInSeconds / 60);

  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);

  return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
};


const AIInsights = () => {
  const [auditType, setAuditType] = useState(null);
  const [modalInfo, setModalInfo] = useState({
    showModal: false,
    modalTitle: "",
    selectedAuditStores: [],
  });

  const {
    loading: auditCycleLoading,
    data: auditCycleData,
    getData: getAuditCyclesData,
  } = useFetchFunction();

  const {
    loading: insightsLoading,
    data: insightsData,
    getData: getInsightsData,
  } = useFetchFunction();

  // Dashboard Visibility: FloorWalk can switch the AI Insights page off for a client
  const { data: widgetAccess, getData: getWidgetAccess } = useFetchFunction();
  const switchedOff = widgetAccess?.ai_insights === false;

  useEffect(() => {
    getWidgetAccess("dashboard_widget_access");
    getAuditCyclesData("audit_cycle_for_dashboard");
  }, []);

  useEffect(() => {
    if (!auditCycleData?.length) return;

    const latestCycle = auditCycleData.reduce((latest, cycle) => {
      return Number(cycle.id) > Number(latest.id) ? cycle : latest;
    });

    setAuditType(latestCycle.id);
  }, [auditCycleData]);

  useEffect(() => {
    if (!auditType || switchedOff) return;
    getInsightsData(`audit_cycle/${auditType}/cycle_ai_insights`);
  }, [auditType, switchedOff]);

  // Group API records by point_key.
  const groupedInsights = useMemo(() => {
    if (!insightsData || typeof insightsData !== "object") {
      return {
        success: [],
        critical: [],
        warning: [],
      };
    }

    return {
      success: Array.isArray(insightsData.went_well)
        ? insightsData.went_well
        : [],

      critical: Array.isArray(insightsData.did_not_go_well)
        ? insightsData.did_not_go_well
        : [],

      warning: Array.isArray(insightsData.can_be_improved)
        ? insightsData.can_be_improved
        : [],
    };
  }, [insightsData]);

  const handleCloseModal = () => {
    setModalInfo({
      showModal: false,
      modalTitle: "",
      selectedAuditStores: [],
    });
  };

  const handleFilterChange = (value) => {
    setAuditType(value);
  };

  const handleViewAudits = (sectionKey, point) => {
    const auditStoreIds =
      point?.audit_stores?.map((auditStore) =>
        Number(auditStore.audit_store_id),
      ) || [];

    setModalInfo({
      showModal: true,
      modalTitle: `${SECTION_CONFIG[sectionKey]?.title || "AI Observations"} in ${
        selectedCycle?.name || "Selected Cycle"
      }`,
      selectedAuditStores: auditStoreIds,
    });
  };

  const selectedCycle = auditCycleData?.find(
    (cycle) => Number(cycle.id) === Number(auditType),
  );

  const isLoading = auditCycleLoading || insightsLoading;

  if (switchedOff) {
    return (
      <div className="aii-page">
        <h2 className="aii-title">AI Insights</h2>
        <p className="aii-subtitle">
          AI Insights is not switched on for your account. Please contact your
          FloorWalk project manager.
        </p>
      </div>
    );
  }

  return (
    <div className="aii-page">
      <header className="aii-header">
        <div className="aii-title-wrap">
          <div className="aii-spark-lg">
            <span className="geminiSparkle">✦</span>
          </div>

          <div>
            <h2 className="aii-title">AI Insights</h2>

            <p className="aii-subtitle">
              Understand the overall customer experience across your audits.
            </p>
          </div>
        </div>

        <div className="aii-filter">
          <AppstoreOutlined className="aii-filter-icon" />

          <span className="aii-filter-label">Type of Audit</span>

          <Select
            className="aii-filter-select"
            value={auditType}
            options={
              auditCycleData?.map((cycle) => ({
                value: cycle.id,
                label: cycle.name,
              })) || []
            }
            onChange={handleFilterChange}
            loading={auditCycleLoading}
            disabled={auditCycleLoading || !auditCycleData?.length}
            popupMatchSelectWidth={false}
          />

          <span className="aii-filter-updated">
            Last updated &nbsp;: &nbsp;
            {getUpdatedTime(insightsData?.modified_ai_analysis_date)}
          </span>
        </div>
      </header>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          <section className="aii-overview">
            <div className="aii-overview-text">
              <div className="aii-spark-sm">
                <span className="geminiSparkle">✦</span>
              </div>

              <div>
                <h3 className="aii-overview-title">Overall Observation</h3>

                <p className="aii-overview-desc">
                  AI observations generated from the selected audit cycle.
                </p>
              </div>
            </div>

            <div className="aii-stats">
              {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
                const total = groupedInsights[sectionKey]?.length || 0;

                return (
                  <div
                    className="aii-stat"
                    key={sectionKey}
                    // onClick={() => handleHeaderCountClick(sectionKey)}
                  >
                    <div className={`aii-stat-icon ${sectionKey}`}>
                      {config.icon}
                    </div>

                    <div>
                      <div className={`aii-stat-count ${sectionKey}`}>
                        {total}
                      </div>

                      <div className="aii-stat-label">
                        {sectionKey === "success"
                          ? "Positive Observations"
                          : sectionKey === "critical"
                            ? "Negative Observations"
                            : "Improvement Areas"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="aii-grid">
            {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
              const points = groupedInsights[sectionKey];

              return (
                <section className={`aii-card ${sectionKey}`} key={sectionKey}>
                  <div className="aii-card-head">
                    <div className={`aii-card-icon ${sectionKey}`}>
                      {config.icon}
                    </div>

                    <div className="aii-card-head-text">
                      <h3 className="aii-card-title">{config.title}</h3>

                      <p className="aii-card-sub">{config.subtitle}</p>
                    </div>

                    <span className={`aii-pill ${sectionKey}`}>
                      {points.reduce(
                        (sum, item) => sum + (item.audit_stores?.length || 0),
                        0,
                      )}
                      &nbsp; Observations
                    </span>
                  </div>

                  <ul className="aii-list">
                    {points.map((item) => (
                      <li
                        className="aii-item"
                        key={`${sectionKey}-${item.point}`}
                      >
                        <p className="aii-item-text">{item.point}</p>

                        <div className="aii-item-meta">
                          <span className={`aii-pill ${sectionKey}`}>
                            {item.audit_stores?.length || 0} audits
                          </span>

                          <button
                            type="button"
                            className="aii-link"
                            onClick={() => handleViewAudits(sectionKey, item)}
                          >
                            View audits <ArrowRightOutlined />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}

      <AIObservationModal
        open={modalInfo.showModal}
        selectedCycleId={auditType}
        title={modalInfo.modalTitle}
        auditStoreIds={modalInfo.selectedAuditStores}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default AIInsights;
